import { getCoreUtils, nz } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppNodeOf } from "./AppNodeOf";
import { AppIndex, AppRow } from "./AppIndex";
import { AppTaskChip, AppTaskRules } from "./AppTaskRules";
import { AppTaskActions, AppTaskDecidedTell, IAppTaskDecided, IAppTaskHeard } from "./AppTaskActions";
import { AppDocListen, AppHold, AppHoldLatch, IRxLater } from "./AppReactions";
import { AppClassList } from "./AppClassList";
import { AppReactRules } from "./AppReactRules";
import { AppTaskApi } from "./AppTaskApi";
import { AppTaskTips } from "./AppTaskTips";
import { AppIcon, AppIcons } from "./AppIcons";
import { AppMessageKeyboard, IMessageElement } from "./AppMessageKeyboard";
import { AppTask } from "./AppTask";
import { AppTaskList, IAppTaskListListener } from "./AppTaskList";
import { AppTaskUi } from "./AppTaskUi";

// AppTaskFrame - task actions ON the contact page. The service draws a task strip under the page's
// header (items carrying `data-task-id`, `data-owner`, `data-due`, `data-state`) and marks the
// bubbles that are a task's evidence with `data-task-id`. This attaches, from OUTSIDE the
// script-free `blob:` frame (the `AppReactions` / `AppMediaFill` way):
//   - on each strip item: a round tick (done / reopen) and a "…" snooze menu (tomorrow, next week);
//   - on a marked bubble: "✓ Mark done" on hover (mouse) or a long press (touch).
// Every change goes through `AppTaskActions` - the ONE sender, one request in flight per task.
// ⭐ DESIGN F (owner, 2026-09-26): the due chip is recoloured in the owner's day ("3 d late" / "Today");
// a reply owed gets no tick (its "…" has Mark done); a tick is done at once with "Undo" for `UndoMs`
// (Undo = reopen through `/tasks`; pressed while the close is in flight, the reopen waits for it), then
// the row leaves the strip; the header's "N open" (`.tcount`) is recounted after every change.
// ⛔ A TICK SENDS NO MESSAGE: the "let <name> know?" card (`AppTaskNotify`, above the composer) is only
// offered on a CONFIRMED close, and only its Send click reaches `AppComposer.SendText`.
//
// ⚠ OPTIMISTIC: the item's `data-state` changes at once (faded while saving); a refusal puts back
// the exact attribute and class it had and says why under the composer, in ink.
// ⛔ Attached only for an index with the new fields (`AppTaskRules.Enabled`) - `AppMain` decides.
// ⛔ Our own words go in by `textContent`; a task id is compared in a loop, never put in a selector.
// ⚠ SHAPED BY INTERFACES, so the suite drives it with a small fake document.

export interface ITfNode {
    className: string;
    textContent: string | null;
    getAttribute(Name: string): string | null;
    setAttribute(Name: string, Value: string): void;
    appendChild(Child: ITfNode): unknown;
    removeChild(Child: ITfNode): unknown;
    insertBefore(Child: ITfNode, Ref: ITfNode | null): unknown;
    querySelector(Selector: string): ITfNode | null;
    closest(Selector: string): ITfNode | null;
}

/** What `NoRoomAbove` measures: an element's box and its chain of parents (the browser's element fits by shape). */
export interface ITfBoxed {
    parentElement?: ITfBoxed | null;
    scrollHeight: number;
    clientHeight: number;
    getBoundingClientRect(): { top: number; width?: number };
}

export interface ITfDoc {
    head: ITfNode | null;
    createElement(Tag: string): ITfNode;
    /** For our SVG icons (`AppIcons.Build`) - built node by node, never parsed from a string. */
    createElementNS(Ns: string, Tag: string): ITfNode;
    querySelectorAll(Selector: string): ArrayLike<ITfNode>;
    addEventListener(Type: string, Listener: (Ev: Event) => void, Capture?: boolean): void;
}

export interface ITfFrame {
    contentDocument: ITfDoc | null;
    addEventListener(Type: string, Listener: () => void): void;
}

export interface ITfEvent {
    target: unknown;
    relatedTarget?: unknown;
    pointerType?: string;
    preventDefault(): void;
}

/** The page half: a sentence under the composer, and the clock. */
export interface ITfHost {
    Say(Text: string, Bad: boolean): void;
    Now(): Date;
}

/** One strip item or bubble changed optimistically, and how to put it back. */
export class AppTaskFrameMark {
    public Node: ITfNode | null = null;
    public OldState: string | null = null;
    public OldClass: string = "";
    /** It stood in the "Done (N)" section when the change began (a refusal puts it back there). */
    public Shelved: boolean = false;
}

export class AppTaskFrame {
    private MouseMoved: boolean = false;
    public static readonly TaskAttr: string = "data-task-id";
    public static readonly StateAttr: string = AppTaskUi.StateAttr;
    public static readonly ActAttr: string = AppTaskUi.ActAttr;
    public static readonly UntilAttr: string = "data-wac-until";
    /** On the bubble's button: the ids it acts on, space-separated. */
    public static readonly IdsAttr: string = "data-wac-tfids";
    public static readonly ControlsClass: string = "wac-tfctl";
    /** Our tick, where the page's own circle (`.tb`) was. */
    public static readonly TickClass: string = AppTaskUi.TickClass;
    /** On an item we decorated - hides the page's circle so there is one, not two. */
    public static readonly OnClass: string = "wac-tfon";
    public static readonly MarkClass: string = "wac-tfmark";
    /** On the bubble's button when it offers Reopen - its tick icon is hidden. */
    public static readonly ReopenClass: string = "wac-tfreo";
    public static readonly PendingClass: string = "wac-tfpending";
    /** Design F: the "Undo" beside a task just closed, for `UndoMs`; then the row leaves the strip. */
    public static readonly UndoClass: string = "wac-tfundo";
    public static readonly GoneClass: string = "wac-tfgone";
    /** On the header's "N open" chip (`.tcount`) when nothing is left open. */
    public static readonly ZeroClass: string = "wac-tfzero";
    public static readonly ReopenButtonClass: string = "wac-tfreopen";
    public static readonly DoneEmptyClass: string = "wac-tdempty";
    public static readonly CountSelector: string = ".tcount";
    /** The tapped "them"/"both" chip's explanation under its row, and the row that holds it. */
    public static readonly NoteClass: string = "wac-tfnote";
    public static readonly NotedClass: string = "wac-tfnoted";
    /** Review 2026-11-01: each open task is a PILL; its label opens the details (`ToggleInfo`). */
    public static readonly InfoAttr: string = "data-wac-tfinfo";
    public static readonly InfoOpenClass: string = "wac-tfopen";
    public static readonly MineClass: string = "wac-tfmine";
    public static readonly MineWords: string = "Your move";
    public static readonly ShowInfoWords: string = "Show details";
    public static readonly UndoMs: number = 6000;
    public static readonly HoldMs: number = 450;

    private Actions: AppTaskActions;
    private Index: AppIndex;
    private Page: string;
    private Host: ITfHost;
    private Coarse: boolean;
    private Timer: IRxLater;
    private Frame: ITfFrame | null = null;
    private Doc: ITfDoc | null = null;
    /** The one "✓ Mark done" button, moved onto whichever marked bubble the pointer is on. */
    private Mark: ITfNode | null = null;
    /** The words on `Mark`, beside its tick icon - the only part `ShowMark` rewrites. */
    private MarkText: ITfNode | null = null;
    /** The bubble `Mark` is on right now, or null while it is off the page (`RestMark`). */
    private MarkOn: ITfNode | null = null;
    public readonly Latch: AppHoldLatch;
    public Stopped: boolean = false;
    /** Task id -> the timer ending its "Undo" (design F). */
    private Undos: Record<string, number> = {};
    /** Task ids whose Undo was pressed while the close was still on its way: reopened once it lands. */
    private Wanted: Record<string, boolean> = {};
    /** The one open chip note (`ToggleNote`), the strip item holding it, and the chip it explains. */
    private Note: ITfNode | null = null;
    private NoteItem: ITfNode | null = null;
    private NoteFor: ITfNode | null = null;
    /** Told when the service CONFIRMS a close or a reopen (the "Let <name> know?" card); null = nobody. */
    public Decided: IAppTaskDecided | null = null;
    /** ⭐ TASKS ARE OBJECTS (2026-11-02): the page's tasks; the "N open" chip and the phone badge listen to it. */
    public readonly List: AppTaskList = new AppTaskList();
    /** One task UI object per strip item. */
    private Uis: AppTaskUi[] = [];

    public constructor(Actions: AppTaskActions, Index: AppIndex, Page: string, Host: ITfHost, Coarse: boolean,
                       Timer: IRxLater) {
        this.Actions = Actions;
        this.Index = Index;
        this.Page = nz(Page);
        this.Host = Host;
        this.Coarse = Coarse;
        this.Timer = Timer;
        this.Latch = new AppHoldLatch(AppTaskFrame.HoldMs, Timer);
        this.List.OnChanged(new AppTaskFrameCount(this));
    }

    public Attach = (Frame: ITfFrame): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Frame = Frame;
            Frame.addEventListener("load", this.OnLoad);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.Attach");
        }
    };

    /**
     * The page is in: the strip's controls and the listeners - once per page. ⛔ NO `<style>` IS ADDED (the CSP
     * has no 'unsafe-inline' for styles): `Css` rides in the frame's linked sheet (`AppMain.Frame`).
     */
    public OnLoad = (): void => {
        AppDocListen.Loaded(this.Stopped, this.Frame, this.Doc !== null, this.OnLoaded, "AppTaskFrame.OnLoad");
    };

    /** The task strip's own per-document load: its decorate gate, its listeners, its keyboard attach. */
    private OnLoaded = (Raw: unknown): void => {
        const Doc: ITfDoc = Raw as ITfDoc;
        this.Doc = Doc;
        if (Doc.querySelectorAll("[" + AppTaskFrame.TaskAttr + "]").length === 0) { return; }
        this.Decorate();
        Doc.addEventListener("click", this.OnClick);
        Doc.addEventListener("keydown", this.OnKey);
        AppDocListen.Pointer(Doc, this.OnOver, this.OnMove, this.OnOut, this.OnDown, this.OnUp);
        AppMessageKeyboard.Attach(Doc, this.OnKeyboardOpen, this.RestMark);
    };

    private OnKeyboardOpen = (Bubble: IMessageElement): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true) { return; }
            this.ShowMark(this.BubbleOf(Bubble));
        } catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.OnKeyboardOpen"); }
    };

    public Stop = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Stopped = true;
            if (this.Latch.Hold !== 0) { this.Timer.Cancel(this.Latch.Hold); }
            this.Latch.Hold = 0;
            for (const Id of Object.keys(this.Undos)) { this.Timer.Cancel(this.Undos[Id]); }
            this.Undos = {};
            this.Wanted = {};
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.Stop");
        }
    };

    // ── The strip's controls ─────────────────────────────────────────────────────────────────

    /**
     * A tick and a snooze menu on every strip item (`li.tk[data-task-id]`, `TaskStateHtml.Item`).
     * ⚠ THE TICK TAKES THE PLACE OF THE PAGE'S OWN CIRCLE (`<i class="tb">`, hidden by `wac-tfon`) -
     * appended beside it, the item showed two circles. The "…" goes at the end.
     * ⚠ A SNOOZE THAT HAS PASSED IS REVEALED HERE: the page ships snoozed items hidden and leaves
     * "has `data-until` come" to the app, because only the browser knows the owner's day.
     */
    private Decorate = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Today: string = AppTaskRules.TodayLocal(this.Host.Now());
            this.List.Clear();
            this.Uis = [];
            for (const Item of this.Items()) { this.UiOf(Item); }
            for (const Item of this.Items()) { this.DecorateItem(Item, Today); }
            this.Recount();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.Decorate");
        }
    };

    /**
     * One strip item (design F): a passed snooze revealed, the tick (⛔ NOT on a reply owed - it is
     * closed by replying; its "…" offers Mark done instead, as the Tasks tab does), the "…" menu, and
     * the due chip recoloured in the owner's day.
     */
    private DecorateItem = (Item: ITfNode, Today: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (AppTaskFrame.InDone(Item) === true) { this.AddReopen(Item); return; }
            const Until: string = nz(Item.getAttribute("data-until"));
            if (nz(Item.getAttribute(AppTaskFrame.StateAttr)) === "snoozed" && AppTaskRules.IsDay(Until) === true
                && Until <= Today) { this.UiOf(Item).Show("open"); }
            const Reply: boolean = AppTaskFrame.IsReplyItem(Item);
            if (Reply === false) {
                const Tick: ITfNode = this.Button("done", "", "", "Mark done");
                this.AddIcon(Tick, AppIcons.Check);
                Tick.className = AppTaskFrame.TickClass;
                Item.insertBefore(Tick, Item.querySelector(".tb"));
                Item.className = AppTaskFrame.Classes(Item.className, AppTaskFrame.OnClass);
            }
            const Box: ITfNode = (this.Doc as ITfDoc).createElement("span");
            Box.className = AppTaskFrame.ControlsClass;
            Box.appendChild(this.SnoozeMenu(Reply));
            Item.appendChild(Box);
            this.MakePill(Item);
            AppTaskFrame.Paint(Item);
            AppTaskFrame.Recolour(Item, Today);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.DecorateItem");
        }
    };

    /** The page's neutral due chip, in the owner's day - drawn by the task UI object. */
    public static Recolour = (Item: ITfNode, Today: string): void => {
        AppTaskUi.Recolour(Item, Today);
    };

    /**
     * The header's "N open" chip tells the truth after every change: the strip items still open (a
     * reply owed counts, as the page counted it). Hidden at 0 (`ZeroClass`).
     */
    public Recount = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Doc === null) { return; }
            this.RecountDone();
            this.List.Settle();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.Recount");
        }
    };

    /** The list changed (`AppTaskFrameCount`): the header's "N open" chip shows its open count. */
    public PaintCount = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Doc === null) { return; }
            const Open: number = this.List.OpenCount();
            const Chips: ArrayLike<ITfNode> = (this.Doc as ITfDoc).querySelectorAll(AppTaskFrame.CountSelector);
            for (let At: number = 0; At < Chips.length; At = At + 1) {
                Chips[At].textContent = String(Open) + " open";
                let Cls: string = AppTaskFrame.Without(Chips[At].className, AppTaskFrame.ZeroClass);
                if (Open === 0) { Cls = AppTaskFrame.Classes(Cls, AppTaskFrame.ZeroClass); }
                Chips[At].className = Cls;
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.PaintCount");
        }
    };

    // ── The "Done (N)" section (owner, 2026-10-22) ────────────────────────────────────────────

    /** DOCUMENTED EXCEPTION: reads one node in hand. ERROR-SUPPRESSED-JUSTIFIED: pure. */
    public static InDone = (Item: ITfNode): boolean => {
        return Item.closest("." + AppTaskTips.DoneClass) !== null;
    };

    /** The section's "Done (N)" follows its items; with none it is hidden (`wac-tdempty`). */
    private RecountDone = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Found: ArrayLike<ITfNode> = (this.Doc as ITfDoc).querySelectorAll("." + AppTaskTips.DoneClass);
            for (let At: number = 0; At < Found.length; At = At + 1) {
                let Count: number = 0;
                for (const Item of this.Items()) {
                    if (Item.closest("." + AppTaskTips.DoneClass) === Found[At]) { Count = Count + 1; }
                }
                const Sum: ITfNode | null = Found[At].querySelector("summary");
                if (Sum !== null) { Sum.textContent = AppTaskTips.DoneTitle(Count); }
                let Cls: string = AppTaskFrame.Without(Found[At].className, AppTaskFrame.DoneEmptyClass);
                if (Count === 0) { Cls = AppTaskFrame.Classes(Cls, AppTaskFrame.DoneEmptyClass); }
                Found[At].className = Cls;
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.RecountDone");
        }
    };

    /** The strip's Done list, made (folded, empty) when the page had none; null with no strip. */
    private DoneList = (): ITfNode | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Doc: ITfDoc = this.Doc as ITfDoc;
            const Lists: ArrayLike<ITfNode> = Doc.querySelectorAll("." + AppTaskTips.DoneListClass);
            if (Lists.length > 0) { return Lists[0]; }
            const Navs: ArrayLike<ITfNode> = Doc.querySelectorAll("nav");
            if (Navs.length === 0) { return null; }
            const Box: ITfNode = Doc.createElement("details");
            Box.className = AppTaskTips.DoneClass;
            const Sum: ITfNode = Doc.createElement("summary");
            Sum.textContent = AppTaskTips.DoneTitle(0);
            Box.appendChild(Sum);
            const List: ITfNode = Doc.createElement("ol");
            List.className = AppTaskTips.DoneListClass;
            Box.appendChild(List);
            Navs[0].appendChild(Box);
            return List;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.DoneList");
            return null;
        }
    };

    /** Our controls off an item (tick, "…", Undo, Reopen) - before it moves between the lists. */
    private Strip = (Item: ITfNode): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            for (const Cls of [AppTaskFrame.TickClass, AppTaskFrame.ControlsClass, AppTaskFrame.UndoClass, AppTaskFrame.ReopenButtonClass]) {
                const Found: ITfNode | null = Item.querySelector("." + Cls);
                if (Found !== null) { Item.removeChild(Found); }
            }
            Item.className = AppTaskFrame.Without(AppTaskFrame.Without(Item.className, AppTaskFrame.OnClass), AppTaskFrame.GoneClass);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.Strip");
        }
    };

    /** A closed item goes to the top of "Done (N)" (newest first) with its Reopen. No strip = it just leaves. */
    public Shelve = (Item: ITfNode): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const List: ITfNode | null = this.DoneList();
            if (List === null) { Item.className = AppTaskFrame.Classes(Item.className, AppTaskFrame.GoneClass); return; }
            this.Strip(Item);
            Item.className = AppTaskFrame.Without(Item.className, "more");
            List.insertBefore(Item, List.querySelector(".tk"));
            this.AddReopen(Item);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.Shelve");
        }
    };

    /** A reopened item goes back to the open list with its tick and "…". */
    private Unshelve = (Item: ITfNode): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Doc: ITfDoc = this.Doc as ITfDoc;
            const Lists: ArrayLike<ITfNode> = Doc.querySelectorAll(".tlist");
            if (Lists.length === 0) { return; }
            this.Strip(Item);
            Lists[0].appendChild(Item);
            const Navs: ArrayLike<ITfNode> = Doc.querySelectorAll("nav");
            if (Navs.length > 0) { Navs[0].className = AppTaskFrame.Without(Navs[0].className, "none"); }
            this.DecorateItem(Item, AppTaskRules.TodayLocal(this.Host.Now()));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.Unshelve");
        }
    };

    /** The visible "Reopen" on a Done item - the same `reopen` as the tick. */
    private AddReopen = (Item: ITfNode): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Item.querySelector("." + AppTaskFrame.ReopenButtonClass) !== null) { return; }
            const Label: string = AppTaskTips.ReopenWord + ": " + nz((Item.querySelector(".tl") as ITfNode | null)?.textContent);
            const Button: ITfNode = this.Button("reopen", "", AppTaskTips.ReopenWord, Label);
            Button.className = "btn ghost sm " + AppTaskFrame.ReopenButtonClass;
            Item.insertBefore(Button, Item.querySelector(".tgo"));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.AddReopen");
        }
    };

    /** Every strip item (not a bubble) on the page. */
    private Items = (): ITfNode[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: ITfNode[] = [];
            if (this.Doc === null) { return Out; }
            const Found: ArrayLike<ITfNode> = (this.Doc as ITfDoc).querySelectorAll("[" + AppTaskFrame.TaskAttr + "]");
            for (let At: number = 0; At < Found.length; At = At + 1) {
                if (AppTaskFrame.IsBubble(Found[At]) === false) { Out.push(Found[At]); }
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.Items");
            return [];
        }
    };

    /** The "…" menu: snooze to tomorrow / next week, Mark done (a reply owed only), Dismiss. */
    private SnoozeMenu = (Reply: boolean): ITfNode => {
        if (getCoreUtils().KErrorMode === true) { return (this.Doc as ITfDoc).createElement("span"); }
        try {
            const Doc: ITfDoc = this.Doc as ITfDoc;
            const Menu: ITfNode = Doc.createElement("details");
            const Sum: ITfNode = Doc.createElement("summary");
            this.AddIcon(Sum, AppIcons.More);
            let Tip: string = AppTaskTips.MenuTip;
            if (Reply === true) { Tip = AppTaskTips.ReplyMenuTip; }
            Sum.setAttribute("title", Tip);
            Sum.setAttribute("aria-label", Tip);
            Menu.appendChild(Sum);
            const Pop: ITfNode = Doc.createElement("span");
            Pop.className = "wac-tfpop";
            const Today: string = AppTaskRules.TodayLocal(this.Host.Now());
            Pop.appendChild(this.Button("snooze", AppTaskRules.SnoozeDay("tomorrow", Today), "Tomorrow", "Snooze until tomorrow"));
            const Next: string = AppTaskRules.SnoozeDay("nextweek", Today);
            Pop.appendChild(this.Button("snooze", Next, "Next week", "Snooze to " + AppTaskRules.DayLabel(Next)));
            if (Reply === true) { Pop.appendChild(this.Button("done", "", "Mark done", "Mark this reply done")); }
            Pop.appendChild(this.Button("dismiss", "", "Dismiss", "Dismiss this task"));
            Menu.appendChild(Pop);
            return Menu;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.SnoozeMenu");
            return (this.Doc as ITfDoc).createElement("span");
        }
    };

    private Button = (Act: string, Until: string, Text: string, Label: string): ITfNode => {
        if (getCoreUtils().KErrorMode === true) { return (this.Doc as ITfDoc).createElement("button"); }
        try {
            const Button: ITfNode = (this.Doc as ITfDoc).createElement("button");
            Button.setAttribute("type", "button");
            Button.setAttribute(AppTaskFrame.ActAttr, Act);
            if (Until.length > 0) { Button.setAttribute(AppTaskFrame.UntilAttr, Until); }
            Button.setAttribute("title", Label);
            Button.setAttribute("aria-label", Label);
            Button.textContent = Text;
            return Button;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.Button");
            return (this.Doc as ITfDoc).createElement("button");
        }
    };

    /** Make an item's tick say what pressing it does now: done -> reopen, open -> done. */
    public static Paint = (Item: ITfNode): void => {
        AppTaskUi.Paint(Item);
    };

    // ── The marked bubbles ───────────────────────────────────────────────────────────────────

    /** A mouse over a marked bubble: "✓ Mark done" moves onto it. ⚠ Not on a touch screen. */
    public OnOver = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true || this.Coarse === true || this.MouseMoved === false) { return; }
            const Bubble: ITfNode | null = this.BubbleOf((Ev as unknown as ITfEvent).target);
            // ⛔ THE BUTTON LEAVES WITH THE POINTER (verifier, 2026-10-12). It used to stay on the last marked bubble for
            // the rest of the visit, hanging 12px into the NEXT message: it covered that message's first line, and a
            // pointer crossing that message met the stale button, which counts as the bubble above - so the reaction
            // opener the owner was reaching for jumped away (measured in Chrome with a raw mouse; the press landed on
            // nothing). The button hangs from its own bubble (a child of it), so the pointer never leaves the bubble on
            // the way to it.
            if (Bubble === null) { this.RestMark(); return; }
            this.ShowMark(Bubble);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.OnOver");
        }
    };

    public OnMove = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.MouseMoved = true;
            this.OnOver(Ev);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.OnMove");
        }
    };

    public OnOut = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Coarse === true) { return; }
            const Next: unknown = (Ev as unknown as ITfEvent).relatedTarget;
            if (this.MarkOn !== null && this.BubbleOf(Next) !== this.MarkOn) { this.RestMark(); }
            if (Next === null || Next === undefined) { this.MouseMoved = false; this.OnUp(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.OnOut");
        }
    };

    /** Take "Mark done" off the page (it is kept, and put on the next marked bubble that is hovered or held). */
    private RestMark = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Mark === null || this.MarkOn === null) { return; }
            const Was: ITfNode = this.MarkOn;
            this.MarkOn = null;
            Was.removeChild(this.Mark as ITfNode);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.RestMark");
        }
    };

    public OnDown = AppHold.Down(this, "AppTaskFrame.OnDown", () => this.OnUp());

    public OnUp = (): void => {
        AppHold.Cancel(this.Latch, false, "AppTaskFrame.OnUp");
    };

    /** Held long enough: the button appears on that bubble. ⚠ Showing it sends nothing. */
    public OnHeld = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Latch.Hold = 0;
            const Bubble: ITfNode | null = this.Latch.HoldOn as ITfNode | null;
            this.Latch.HoldOn = null;
            if (this.Stopped === true) { return; }
            this.ShowMark(Bubble);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.OnHeld");
        }
    };

    /** On the button when it sits inside its bubble's top edge (no room to hang above it). */
    public static readonly InsideClass: string = "wac-tfin";
    /** The room (px) a hanging button needs above its bubble inside the scrolling conversation. */
    public static readonly HangRoom: number = 16;

    /**
     * Is the bubble so near the top of the pane that scrolls it that a button hanging on its top edge would be cut?
     * Measured against the nearest ancestor that scrolls. Anything that cannot be measured (a test's plain node) = room.
     */
    public static NoRoomAbove = (Bubble: unknown): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            return AppTaskFrame.RoomAbove(Bubble) < AppTaskFrame.HangRoom;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.NoRoomAbove");
            return false;
        }
    };

    /** What `RoomAbove` answers for something that cannot be measured (a test's plain node): all the room there is. */
    public static readonly AllRoom: number = 100000;

    /** The px between the bubble's top edge and the top of the nearest ancestor that scrolls (`AllRoom` = not measurable). */
    public static RoomAbove = (Bubble: unknown): number => {
        if (getCoreUtils().KErrorMode === true) { return AppTaskFrame.AllRoom; }
        try {
            const El: ITfBoxed | null = Bubble as ITfBoxed | null;
            if (El === null || El === undefined || typeof El.getBoundingClientRect !== "function") { return AppTaskFrame.AllRoom; }
            let Top: number = 0;
            for (let Up: ITfBoxed | null | undefined = El.parentElement; Up !== null && Up !== undefined; Up = Up.parentElement) {
                if (typeof Up.getBoundingClientRect !== "function" || !(Up.scrollHeight > Up.clientHeight + 1)) { continue; }
                Top = Up.getBoundingClientRect().top;
                break;
            }
            return El.getBoundingClientRect().top - Top;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.RoomAbove");
            return AppTaskFrame.AllRoom;
        }
    };

    /** On the button when it stands wholly ABOVE a bubble too narrow to hold it (`PlaceOf`). */
    public static readonly UpClass: string = "wac-tfup";
    /** ... wholly BELOW it: too narrow, and at the top of what the conversation shows. */
    public static readonly DownClass: string = "wac-tfdn";
    /** How far in from the bubble's side edge the hanging button stops (`Css`) - clear of the reaction opener's reach. */
    public static readonly SideInset: number = 16;
    /** The reaction opener's unseen reach INTO the far side of the bubble (`AppReactions.Css`: `inset:-8px`). */
    public static readonly OpenerReach: number = 8;

    /**
     * ⛔ WHERE "MARK DONE" GOES SO THAT IT NEVER MEETS THE REACTION OPENER (2026-10-13). The opener stands BESIDE the
     * bubble at mid height (right of theirs, left of the owner's); the button hangs on the top edge, `SideInset` in from
     * the right. On a bubble wide enough for it the two are apart by construction. On a NARROW one ("Ok", a one-line
     * reply) the hanging button reached past the bubble's far edge - over the owner's opener, or out of the pane on the
     * left of a contact's message - so there it stands wholly above the bubble (`UpClass`), or wholly below it when the
     * bubble is at the top of what the conversation shows (`DownClass`). A wide bubble with no room above keeps the
     * button just inside its edge (`InsideClass`), as before. The answer is " <class>" or "".
     * `BubbleW` / `MarkW` 0 = not measurable (a test's plain node): wide enough.
     */
    public static PlaceOf = (BubbleW: number, MarkW: number, Room: number): string => {
        if (getCoreUtils().KErrorMode === true) { return ""; }
        try {
            const Narrow: boolean = BubbleW > 0 && MarkW > 0 && BubbleW < MarkW + AppTaskFrame.SideInset + AppTaskFrame.OpenerReach;
            if (Narrow === true) {
                if (Room < AppReactRules.HangPx + 4) { return " " + AppTaskFrame.DownClass; }
                return " " + AppTaskFrame.UpClass;
            }
            if (Room < AppTaskFrame.HangRoom) { return " " + AppTaskFrame.InsideClass; }
            return "";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.PlaceOf");
            return "";
        }
    };

    /** An element's drawn width, or 0 when it cannot be measured. */
    private static WidthOf = (Node: unknown): number => {
        if (getCoreUtils().KErrorMode === true) { return 0; }
        try {
            const El: ITfBoxed | null = Node as ITfBoxed | null;
            if (El === null || El === undefined || typeof El.getBoundingClientRect !== "function") { return 0; }
            const Width: number | undefined = El.getBoundingClientRect().width;
            if (typeof Width !== "number" || isNaN(Width) === true) { return 0; }
            return Width;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.WidthOf");
            return 0;
        }
    };

    private ShowMark = (Bubble: ITfNode | null): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Bubble === null || this.Doc === null) { return; }
            // ⚠ ONE MESSAGE CAN BE THE SOURCE OF SEVERAL TASKS (`TaskStateHtml.Marks`: space-separated
            // ids). The button acts on every one still open; once all are done it offers Reopen for all.
            const Ids: string[] = AppTaskFrame.IdsOf(Bubble.getAttribute(AppTaskFrame.TaskAttr));
            const Open: string[] = [];
            for (const One of Ids) { if (this.IsDecided(One) === false) { Open.push(One); } }
            if (Ids.length === 0) { return; }
            if (this.Mark === null) { this.MakeMark(); }
            const Mark: ITfNode = this.Mark as ITfNode;
            let Act: string = "done";
            let Targets: string[] = Open;
            let Text: string = "Mark done";
            if (Open.length > 1) { Text = "Mark " + String(Open.length) + " tasks done"; }
            if (Open.length === 0) { Act = "reopen"; Targets = Ids; Text = "Reopen task"; }
            if (Open.length === 0 && Ids.length > 1) { Text = "Reopen " + String(Ids.length) + " tasks"; }
            // ⛔ NOT TOUCHED WHILE IT ALREADY SAYS THIS ON THIS BUBBLE (P0 found by the audit, 2026-10-12 - the reaction
            // opener's bug, `AppReactions.OnOver`). Every `mouseover` - the pointer reaching the button itself is one -
            // rewrote and re-appended the button: the element under the pointer left the page and came back, which fires
            // `mouseover` again (measured in Chrome: an endless out/over/append loop while the pointer rests there), and
            // Chrome dispatches NO `mousedown` or `click` for a press on it. "Mark done" could not be pressed with a mouse.
            if (this.MarkOn === Bubble && Mark.getAttribute(AppTaskFrame.ActAttr) === Act
                && Mark.getAttribute(AppTaskFrame.IdsAttr) === Targets.join(" ")) { return; }
            let Cls: string = AppTaskFrame.MarkClass;
            if (Act === "reopen") { Cls = Cls + " " + AppTaskFrame.ReopenClass; }
            Mark.setAttribute(AppTaskFrame.ActAttr, Act);
            Mark.setAttribute(AppTaskFrame.IdsAttr, Targets.join(" "));
            Mark.setAttribute("aria-label", Text);
            // ⚠ ONLY THE WORDS CHANGE: the tick icon was built once (`MakeMark`) and is hidden on a Reopen (`ReopenClass`).
            if (this.MarkText !== null) { (this.MarkText as ITfNode).textContent = Text; }
            this.SeatMark(Bubble, Mark, Cls);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.ShowMark");
        }
    };

    /**
     * Put the button on its bubble and give it its place (`PlaceOf`: hanging on the top edge, just inside it, or wholly
     * above / below a narrow bubble). ⚠ Already on this bubble (its task changed since): only the class may change -
     * never out and back in (the 2026-10-12 P0). The place is measured with the button ON the bubble (its own width is
     * part of the rule), in the same task as the append - nothing is painted in between.
     */
    private SeatMark = (Bubble: ITfNode, Mark: ITfNode, Cls: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.MarkOn !== Bubble) {
                Mark.className = Cls;
                this.MarkOn = Bubble;
                Bubble.appendChild(Mark);
            }
            const Placed: string = Cls + AppTaskFrame.PlaceOf(AppTaskFrame.WidthOf(Bubble), AppTaskFrame.WidthOf(Mark), AppTaskFrame.RoomAbove(Bubble));
            if (Mark.className !== Placed) { Mark.className = Placed; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.SeatMark");
        }
    };

    /** The bubble's "Mark done" button: our tick icon (`AppIcons.Check`, was ✓) and a span for the words. */
    private MakeMark = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Mark: ITfNode = this.Button("done", "", "", "Mark this task done");
            this.AddIcon(Mark, AppIcons.Check);
            const Words: ITfNode = (this.Doc as ITfDoc).createElement("span");
            Mark.appendChild(Words);
            this.Mark = Mark;
            this.MarkText = Words;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.MakeMark");
        }
    };

    /** Our SVG icon into a control - built node by node (`AppIcons.Build`), a constant of ours. */
    private AddIcon = (Into: ITfNode, Icon: AppIcon): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Doc === null) { return; }
            const Node: ITfNode | null = AppIcons.Build(this.Doc as ITfDoc, Icon, 0) as ITfNode | null;
            if (Node !== null) { Into.appendChild(Node as ITfNode); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.AddIcon");
        }
    };

    /** The marked bubble an event is on, or null. */
    public BubbleOf = (Target: unknown): ITfNode | null => {
        if (getCoreUtils().KErrorMode === true) { return null; }
        try {
            const Node: ITfNode | null = AppTaskFrame.NodeOf(Target);
            if (Node === null) { return null; }
            const Bubble: ITfNode | null = Node.closest(".b");
            if (Bubble === null || nz(Bubble.getAttribute(AppTaskFrame.TaskAttr)).length === 0) { return null; }
            return Bubble;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.BubbleOf");
            return null;
        }
    };

    // ── Acting ───────────────────────────────────────────────────────────────────────────────

    /** A click on one of our buttons - the ONLY way anything here changes a task. */
    public OnClick = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true) { return; }
            const Node: ITfNode | null = AppTaskFrame.NodeOf((Ev as unknown as ITfEvent).target);
            if (this.ToggleNote(Node) === true || Node === null) { return; }
            if (AppTaskFrame.ToggleInfo(Node) === true) { return; }
            const Button: ITfNode | null = Node.closest("[" + AppTaskFrame.ActAttr + "]");
            // ⚠ A TOUCH SCREEN HAS NO POINTER TO LEAVE WITH: a tap anywhere but the held bubble puts the button away.
            if (this.Coarse === true && this.MarkOn !== null && Node.closest(".b") !== this.MarkOn) { this.RestMark(); }
            if (Button === null) { return; }
            const Owner: ITfNode | null = Button.closest("[" + AppTaskFrame.TaskAttr + "]");
            if (Owner === null) { return; }
            (Ev as unknown as ITfEvent).preventDefault();
            // ⚠ THE BUBBLE'S BUTTON NAMES ITS OWN TARGETS (`IdsAttr`); a strip item is one id.
            let Ids: string[] = AppTaskFrame.IdsOf(Button.getAttribute(AppTaskFrame.IdsAttr));
            if (Button.getAttribute(AppTaskFrame.IdsAttr) === null) { Ids = AppTaskFrame.IdsOf(Owner.getAttribute(AppTaskFrame.TaskAttr)); }
            for (const Id of Ids) {
                this.Act(Id, nz(Button.getAttribute(AppTaskFrame.ActAttr)), nz(Button.getAttribute(AppTaskFrame.UntilAttr)));
            }
            // ⚠ PRESSED = DONE WITH: the bubble's button goes away (it would go on saying "Mark done" over a task that
            // is now done, and a second press would ask for the same thing again). The next hover offers the way back.
            if (Button === this.Mark) { this.RestMark(); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.OnClick");
        }
    };

    /**
     * The pill (review 2026-11-01): its label becomes the button that shows the details (owner chip, due chip, the
     * link to its message, the "…" menu - hidden on the closed pill by `Css`). A task that is the owner's own move
     * has no "who" chip on the page, so the details say "Your move". ⚠ Shows words; sends nothing.
     */
    private MakePill = (Item: ITfNode): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            const Label: ITfNode | null = Item.querySelector(".tl");
            if (Label === null) { return; }
            Label.setAttribute(AppTaskFrame.InfoAttr, "1");
            Label.setAttribute("role", "button");
            Label.setAttribute("tabindex", "0");
            Label.setAttribute("aria-expanded", "false");
            if (nz(Label.getAttribute("title")).length === 0) { Label.setAttribute("title", AppTaskFrame.ShowInfoWords); }
            if (Item.querySelector(".who") !== null || Item.querySelector("." + AppTaskFrame.MineClass) !== null) { return; }
            const Mine: ITfNode = (this.Doc as ITfDoc).createElement("span");
            Mine.className = AppTaskFrame.MineClass;
            Mine.textContent = AppTaskFrame.MineWords;
            Item.insertBefore(Mine, Item.querySelector(".tgo"));
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.MakePill");
        }
    };

    /** A press on a pill's label opens or closes its details. True when the press was on one (nothing else acts). */
    public static ToggleInfo = (Node: ITfNode): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Label: ITfNode | null = Node.closest("[" + AppTaskFrame.InfoAttr + "]");
            if (Label === null) { return false; }
            const Item: ITfNode | null = Label.closest("[" + AppTaskFrame.TaskAttr + "]");
            if (Item === null) { return false; }
            const Open: boolean = Label.getAttribute("aria-expanded") !== "true";
            Label.setAttribute("aria-expanded", String(Open));
            if (Open === true) { Item.className = AppTaskFrame.Classes(Item.className, AppTaskFrame.InfoOpenClass); }
            else { Item.className = AppTaskFrame.Without(Item.className, AppTaskFrame.InfoOpenClass); }
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.ToggleInfo");
            return false;
        }
    };

    /** Enter or Space on a pill's label = the press (it is a `role=button` span). */
    public OnKey = (Ev: Event): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true) { return; }
            const Key: string = nz((Ev as unknown as { key: string }).key);
            if (Key !== "Enter" && Key !== " ") { return; }
            const Node: ITfNode | null = AppTaskFrame.NodeOf((Ev as unknown as ITfEvent).target);
            if (Node === null || Node.getAttribute(AppTaskFrame.InfoAttr) === null) { return; }
            (Ev as unknown as ITfEvent).preventDefault();
            AppTaskFrame.ToggleInfo(Node);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.OnKey");
        }
    };

    /**
     * TOUCH HAS NO HOVER, so a tap on a strip item's "them"/"both" chip (`TaskStateHtml.WhoHint`) shows
     * its tooltip - the page's own `title`, so the words stay the service's (`AppTaskTips.Explain`) - as a
     * small note under that row. ANY next tap removes it (tapping the same chip again just closes it).
     * Answers true when the tap was on a chip, so nothing else acts on it. ⚠ Shows words; sends nothing.
     */
    public ToggleNote = (Node: ITfNode | null): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Was: ITfNode | null = this.NoteFor;
            if (this.Note !== null && this.NoteItem !== null) {
                (this.NoteItem as ITfNode).removeChild(this.Note as ITfNode);
                this.NoteItem.className = AppTaskFrame.Without(this.NoteItem.className, AppTaskFrame.NotedClass);
            }
            this.Note = null; this.NoteItem = null; this.NoteFor = null;
            if (Node === null) { return false; }
            const Chip: ITfNode | null = Node.closest(".who");
            if (Chip === null) { return false; }
            const Item: ITfNode | null = Chip.closest("[" + AppTaskFrame.TaskAttr + "]");
            const Tip: string = nz(Chip.getAttribute("title"));
            if (Item === null || AppTaskFrame.IsBubble(Item) === true || Tip.length === 0) { return false; }
            if (Was === Chip) { return true; }
            const Note: ITfNode = (this.Doc as ITfDoc).createElement("span");
            Note.className = AppTaskFrame.NoteClass;
            Note.setAttribute("role", "note");
            Note.textContent = Tip;
            Item.appendChild(Note);
            Item.className = AppTaskFrame.Classes(Item.className, AppTaskFrame.NotedClass);
            this.Note = Note; this.NoteItem = Item; this.NoteFor = Chip;
            return true;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.ToggleNote");
            return false;
        }
    };

    /** Change `Id` on the page (and in the index) at once, then send; a refusal puts both back. */
    public Act = (Id: string, Action: string, Until: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Action === "undo") { this.Undo(Id); return; }
            if (Id.length === 0 || ["done", "reopen", "snooze", "dismiss"].indexOf(Action) < 0) { return; }
            if (this.Actions.Busy(Action, Id) === true) { this.Host.Say("Still saving the last change to this task - one moment.", false); return; }
            this.DropUndo(Id);
            const Marks: AppTaskFrameMark[] = this.Change(Id, Action);
            // ⭐ OPTIMISTIC: a Done item reopened is back in the open list at once (a refusal shelves it again).
            if (Action === "reopen") {
                for (const Mark of Marks) { if (Mark.Shelved === true && Mark.Node !== null) { this.Unshelve(Mark.Node as ITfNode); } }
                this.Recount();
            }
            const Row: AppRow | null = AppTaskRules.FindById(this.Index, Id);
            let Before: AppRow | null = null;
            if (Row !== null) {
                Before = AppTaskRules.Copy(Row);
                AppTaskRules.Apply(Row, Action, Until, this.Host.Now().toISOString());
            }
            const Heard: AppTaskFrameHeard = new AppTaskFrameHeard(this, Marks, Row, Before, Action, Until, Id);
            const Why: string = this.Actions.Send(this.Page, Action, Id, Until, null, Heard);
            if (Why.length > 0) { Heard.Failed(Why); return; }
            if (Action === "done" || Action === "dismiss") { this.OfferUndo(Id); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.Act");
        }
    };

    // ── Undo (design F) ──────────────────────────────────────────────────────────────────────

    /**
     * A task just closed shows "Undo" for `UndoMs`, then leaves the strip (`Expire`). ⚠ SHOWN AT ONCE,
     * with the close still on its way - the owner sees "done" the moment they tick.
     */
    private OfferUndo = (Id: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            for (const Item of this.ItemsOf(Id)) {
                const Undo: ITfNode = this.Button("undo", "", "Undo", "Undo - reopen this task");
                Undo.className = AppTaskFrame.UndoClass;
                Item.insertBefore(Undo, Item.querySelector(".tgo"));
            }
            this.Undos[Id] = this.Timer.Later(AppTaskFrame.UndoMs, new AppTaskFrameUndoEnd(this, Id).Fire);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.OfferUndo");
        }
    };

    /**
     * Undo pressed: reopen through `/tasks`, the same path as the tick. ⚠ If the close has not landed
     * yet, the reopen waits for it (`Wanted`) - two changes racing on one task land in either order.
     */
    public Undo = (Id: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Undos[Id] === undefined) { return; }
            this.DropUndo(Id);
            if (this.Actions.Busy("reopen", Id) === true) {
                this.Wanted[Id] = true;
                this.Host.Say("Reopening as soon as the change is saved.", false);
                return;
            }
            this.Act(Id, "reopen", "");
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.Undo");
        }
    };

    /** The Undo window ended: the button goes and a task still closed leaves the strip. */
    public Expire = (Id: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Undos[Id] === undefined) { return; }
            this.DropUndo(Id);
            for (const Item of this.ItemsOf(Id)) {
                const State: string = nz(Item.getAttribute(AppTaskFrame.StateAttr));
                if (State === "done" || State === "dismissed") { this.Shelve(Item); }
            }
            this.Recount();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.Expire");
        }
    };

    /** No Undo for `Id` any more: its timer cancelled, its buttons removed. */
    private DropUndo = (Id: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Undos[Id] !== undefined) { this.Timer.Cancel(this.Undos[Id]); }
            delete this.Undos[Id];
            for (const Item of this.ItemsOf(Id)) {
                const Button: ITfNode | null = Item.querySelector("." + AppTaskFrame.UndoClass);
                if (Button !== null) { Item.removeChild(Button); }
            }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.DropUndo");
        }
    };

    /** The strip items (not bubbles) carrying `Id`. */
    private ItemsOf = (Id: string): ITfNode[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            const Out: ITfNode[] = [];
            for (const Item of this.Items()) {
                if (nz(Item.getAttribute(AppTaskFrame.TaskAttr)) === Id) { Out.push(Item); }
            }
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.ItemsOf");
            return [];
        }
    };

    /** Every strip item carrying `Id` takes its new state, faded; what it was is kept. */
    private Change = (Id: string, Action: string): AppTaskFrameMark[] => {
        if (getCoreUtils().KErrorMode === true) { return []; }
        try {
            let State: string = "open";
            if (Action === "done") { State = "done"; }
            if (Action === "dismiss") { State = "dismissed"; }
            if (Action === "snooze") { State = "snoozed"; }
            const Out: AppTaskFrameMark[] = [];
            for (const Item of this.Items()) {
                if (nz(Item.getAttribute(AppTaskFrame.TaskAttr)) !== Id) { continue; }
                const Mark: AppTaskFrameMark = new AppTaskFrameMark();
                Mark.Node = Item;
                Mark.OldState = Item.getAttribute(AppTaskFrame.StateAttr);
                Mark.OldClass = Item.className;
                Mark.Shelved = AppTaskFrame.InDone(Item);
                Item.className = AppTaskFrame.Classes(Item.className, AppTaskFrame.PendingClass);
                this.UiOf(Item).Show(State);
                Out.push(Mark);
            }
            this.Recount();
            return Out;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.Change");
            return [];
        }
    };

    /**
     * Landed: the faded look goes; the owner hears one short line. `Row` is the index's row (null when
     * the index does not know the id) - a confirmed close of a known row is told to `Decided`.
     */
    public Landed = (Marks: AppTaskFrameMark[], Action: string, Until: string, Row: AppRow | null = null,
                     Id: string = "", Changed: boolean = true): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            for (const Mark of Marks) {
                if (Mark.Node === null) { continue; }
                const Node: ITfNode = Mark.Node as ITfNode;
                Node.className = AppTaskFrame.Without(Node.className, AppTaskFrame.PendingClass);
            }
            if (this.Stopped === true) { return; }
            // ⚠ UNDO PRESSED WHILE THE CLOSE WAS ON ITS WAY: reopen now, and offer no update card for a
            // close the owner already took back.
            if ((Action === "done" || Action === "dismiss") && this.Wanted[Id] === true) {
                delete this.Wanted[Id];
                this.Act(Id, "reopen", "");
                return;
            }
            this.Host.Say(AppTaskRules.DoneWord(Action, Until), false);
            AppTaskDecidedTell.Tell(this.Decided, Row, Action, Changed);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.Landed");
        }
    };

    /** Refused: each item gets back EXACTLY the state attribute and class it had; `Why` in ink. */
    public Refused = (Marks: AppTaskFrameMark[], Why: string, Id: string = ""): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.DropUndo(Id);
            delete this.Wanted[Id];
            for (const Mark of Marks) {
                if (Mark.Node === null) { continue; }
                const Node: ITfNode = Mark.Node as ITfNode;
                if (Mark.OldState !== null) { this.UiOf(Node).Show(Mark.OldState); }
                if (Mark.Shelved === true && AppTaskFrame.InDone(Node) === false) { this.Shelve(Node); }
                if (Mark.Shelved === false && AppTaskFrame.InDone(Node) === true) { this.Unshelve(Node); }
                Node.className = Mark.OldClass;
                AppTaskFrame.Paint(Node);
            }
            this.Recount();
            if (this.Stopped === true) { return; }
            this.Host.Say(Why, true);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.Refused");
        }
    };

    /**
     * ⭐ SEVERAL TABS (2026-09-27): another tab's CONFIRMED change to `Id` - the strip items take `State`
     * (a closed one leaves the strip, as after its Undo window) and the "N open" chip follows. ⛔ SHOWN
     * ONLY: nothing is sent, and a change of this tab's own still on its way is left alone.
     */
    public Mirror = (Id: string, State: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Stopped === true || this.Actions.Busy("done", Id) === true) { return; }
            if (["open", "snoozed", "done", "dismissed"].indexOf(State) < 0) { return; }
            this.DropUndo(Id);
            delete this.Wanted[Id];
            for (const Item of this.ItemsOf(Id)) {
                this.UiOf(Item).Show(State);
                let Cls: string = AppTaskFrame.Without(Item.className, AppTaskFrame.PendingClass);
                Cls = AppTaskFrame.Without(Cls, AppTaskFrame.GoneClass);
                Item.className = Cls;
                const Closed: boolean = State === "done" || State === "dismissed";
                if (Closed === true && AppTaskFrame.InDone(Item) === false) { this.Shelve(Item); }
                if (Closed === false && AppTaskFrame.InDone(Item) === true) { this.Unshelve(Item); }
                AppTaskFrame.Paint(Item);
            }
            this.Recount();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.Mirror");
        }
    };

    /** Is the task decided (done or dismissed) - by the index, else by its strip item? */
    private IsDecided = (Id: string): boolean => {
        if (getCoreUtils().KErrorMode === true) { return false; }
        try {
            const Row: AppRow | null = AppTaskRules.FindById(this.Index, Id);
            let State: string = "";
            if (Row !== null) { State = AppTaskRules.StateOf(Row as AppRow, AppTaskRules.TodayLocal(this.Host.Now())); }
            for (const Item of this.Items()) {
                if (nz(Item.getAttribute(AppTaskFrame.TaskAttr)) === Id) { State = nz(Item.getAttribute(AppTaskFrame.StateAttr)); }
            }
            return State === "done" || State === "dismissed";
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.IsDecided");
            return false;
        }
    };

    /** The task UI object drawing `Item` - made (with its task, one per id) the first time it is asked for. */
    private UiOf = (Item: ITfNode): AppTaskUi => {
        const Spare: AppTaskUi = new AppTaskUi(new AppTask(""), Item, new AppTaskList());
        if (getCoreUtils().KErrorMode === true) { return Spare; }
        try {
            for (const Ui of this.Uis) {
                if (Ui.Node === Item) { return Ui; }
            }
            let Task: AppTask | null = this.List.ById(nz(Item.getAttribute(AppTaskFrame.TaskAttr)));
            if (Task === null) {
                Task = AppTask.FromStripItem(Item);
                this.List.Add(Task);
            }
            const Made: AppTaskUi = new AppTaskUi(Task, Item, this.List);
            this.Uis.push(Made);
            return Made;
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrame.UiOf");
            return Spare;
        }
    };

    // ── Small helpers ────────────────────────────────────────────────────────────────────────

    /**
     * The task ids in a `data-task-id` value - space-separated on a bubble - each checked against the
     * contract's allow-list (`AppTaskApi.IdOk`); anything else is dropped, never sent.
     * DOCUMENTED EXCEPTION: pure string arithmetic. ERROR-SUPPRESSED-JUSTIFIED: [] IS the safe answer.
     */
    public static IdsOf = (Raw: string | null): string[] => {
        const Out: string[] = [];
        for (const One of nz(Raw).split(/\s+/)) {
            if (AppTaskApi.IdOk(One) === true && Out.indexOf(One) < 0) { Out.push(One); }
        }
        return Out;
    };

    /**
     * A reply owed (`TaskStateHtml`: `data-source="reply"`, class `rp`) - closed by replying, so no tick.
     * DOCUMENTED EXCEPTION: reads one node in hand. ERROR-SUPPRESSED-JUSTIFIED: pure.
     */
    public static IsReplyItem = (Node: ITfNode): boolean => {
        return nz(Node.getAttribute("data-source")) === "reply" || (" " + nz(Node.className) + " ").indexOf(" rp ") >= 0;
    };

    /** DOCUMENTED EXCEPTION: reads one class list in hand. ERROR-SUPPRESSED-JUSTIFIED: pure. */
    public static IsBubble = (Node: ITfNode): boolean => {
        return (" " + nz(Node.className) + " ").indexOf(" b ") >= 0;
    };

    /** DOCUMENTED EXCEPTION: pure string arithmetic. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static Classes = (List: string, Add: string): string => {
        return (AppTaskFrame.Without(List, Add) + " " + Add).trim();
    };

    /** DOCUMENTED EXCEPTION: pure string arithmetic. ERROR-SUPPRESSED-JUSTIFIED: cannot throw. */
    public static Without = (List: string, Drop: string): string => {
        return AppClassList.Tokens(nz(List)).filter((One: string) => One !== Drop).join(" ");
    };

    /**
     * DOCUMENTED EXCEPTION: a type probe over a value in hand.
     * ERROR-SUPPRESSED-JUSTIFIED: null IS the answer for anything that is not an element.
     */
    private static NodeOf = (Target: unknown): ITfNode | null => {
        return AppNodeOf.Read<ITfNode>(Target);
    };

    /**
     * The task actions' stylesheet - linked into every contact frame by `AppMain.Frame`.
     * DOCUMENTED EXCEPTION: a constant stylesheet. ERROR-SUPPRESSED-JUSTIFIED: returns a literal.
     */
    public static Css = (): string => {
        return AppTaskTips.DoneCss() + ".wac-tfon>.tb{display:none}"
            + ".wac-tfctl{display:inline-flex;align-items:center;gap:4px;margin-left:2px;vertical-align:middle}"
            + ".wac-tftick{flex:none;width:22px;height:22px;border-radius:50%;border:2px solid #9aa2b1;"
            + "background:transparent;color:transparent;font-size:12px;line-height:18px;padding:0;cursor:pointer}"
            + ".wac-tftick:hover,.wac-tftick:focus-visible{border-color:var(--live,#4f8035);color:var(--live,#4f8035);outline:0}"
            + ".wac-tftick[aria-pressed=true]{background:var(--live,#4f8035);border-color:var(--live,#4f8035);color:var(--bg,#fff)}"
            + ".wac-tfctl details{position:relative;display:inline-block}"
            + ".wac-tfctl summary{list-style:none;cursor:pointer;padding:0 6px;border-radius:4px;color:var(--dim,#6b7385)}"
            + ".wac-tfctl summary::-webkit-details-marker{display:none}"
            + ".wac-tfpop{position:absolute;right:0;top:100%;z-index:30;display:flex;flex-direction:column;min-width:130px;"
            // The menu floats over the page: an edged rectangle, the one shadow here (business look, 2026-10-13).
            + "padding:4px;border:1px solid var(--line,#e3e6ec);border-radius:6px;background:var(--inbg,#fff);"
            + "box-shadow:0 4px 16px rgba(16,20,28,.2)}"
            + ".wac-tfpop button{border:0;background:transparent;text-align:left;padding:7px 10px;border-radius:4px;"
            + "cursor:pointer;font:inherit;color:inherit}.wac-tfpop button:hover{background:rgba(11,20,26,.07)}"
            + ".tk[data-state=done] .tl,.tk[data-state=dismissed] .tl{text-decoration:line-through;color:var(--dim,#6b7385)}"
            + ".tk[data-state=done] .due,.tk[data-state=dismissed] .due,.tk[data-state=done] .who{opacity:.5}"
            + ".tk.wac-tfgone,.tcount.wac-tfzero{display:none!important}"
            + ".wac-tfundo{flex:none;margin-left:auto;padding:2px 10px;border:1px solid #d8dce4;border-radius:6px;"
            + "background:var(--bg,#fff);color:inherit;font:inherit;font-size:13px;font-weight:600;cursor:pointer}"
            + ".wac-tfundo+.tgo{margin-left:0}"
            + ".tk .due.u-overdue{background:#fde8e6;color:#b42318}.tk .due.u-today{background:#e8f0fd;color:#1f5fbf}"
            // ⭐ THE OWNER'S THEME CHOICE (`AppTheme`, 2026-09-30): the frame's <html> carries data-theme, so the system's
            // dark mode applies only when the owner did not choose light, and a chosen dark applies whatever the system says.
            + "@media(prefers-color-scheme:dark){html:not([data-theme=light]) .tk .due.u-overdue{background:#3a1e1b;color:#f2a7a0}"
            + "html:not([data-theme=light]) .tk .due.u-today{background:#1c2a44;color:#8fb3ef}"
            + "html:not([data-theme=light]) .wac-tfundo{border-color:#3a4254}}"
            + "html[data-theme=dark] .tk .due.u-overdue{background:#3a1e1b;color:#f2a7a0}"
            + "html[data-theme=dark] .tk .due.u-today{background:#1c2a44;color:#8fb3ef}html[data-theme=dark] .wac-tfundo{border-color:#3a4254}"
            + ".wac-tfpending{opacity:.55}"
            + ".tk .who[title]{cursor:help}.tk.wac-tfnoted{flex-wrap:wrap}"
            + ".wac-tfnote{flex:0 0 100%;order:9;padding:0 0 2px 30px;font-size:12px;color:var(--dim,#6b7385);white-space:normal}"
            // ⛔ NO SPACE IS RESERVED FOR "Mark done" (business look wave B, 2026-10-13). The rule here was
            // `.b[data-task-id]{padding-top:34px}`: every bubble an open task came from was drawn with 34px of nothing above
            // its words, whether or not the pointer was anywhere near it. The button now HANGS ON THE BUBBLE'S TOP EDGE
            // (half outside it, over the gap above), so it covers no word and the bubble is as tall as its text.
            // ⚠ Still a child of the bubble, shown and put away exactly as before (`OnOver` / `OnOut` / the hold on touch).
            // ⛔ `SideInset` in from the side (it was 8px = exactly where the reaction opener's unseen reach ends).
            + ".b .wac-tfmark{position:absolute;top:-13px;right:" + String(AppTaskFrame.SideInset) + "px;z-index:3;border:0;border-radius:4px;padding:2px 9px;"
            + "font-size:12px;line-height:18px;white-space:nowrap;background:var(--acc,#095495);color:var(--bg,#fff);cursor:pointer}"
            + ".b .wac-tfmark.wac-tfin{top:3px}"
            // ⛔ A NARROW BUBBLE (`PlaceOf`): the button stands wholly above it (or below, with no room above), starting at
            // the bubble's own outer edge - never over the reaction opener beside the bubble, never out of the pane. The
            // unseen strip (`::before`) joins it to its bubble, so the pointer never leaves the bubble on the way to it.
            // `AppReactRules.HangPx` is the band the reaction row keeps clear of.
            + ".b .wac-tfmark.wac-tfup{top:-" + String(AppReactRules.HangPx) + "px;left:0;right:auto}"
            + ".b .wac-tfmark.wac-tfdn{top:auto;bottom:-" + String(AppReactRules.HangPx) + "px;left:0;right:auto}"
            + ".b.me .wac-tfmark.wac-tfup,.b.me .wac-tfmark.wac-tfdn{left:auto;right:0}"
            + ".b .wac-tfmark.wac-tfup::before,.b .wac-tfmark.wac-tfdn::before{content:'';position:absolute;left:0;right:0;height:8px}"
            + ".b .wac-tfmark.wac-tfup::before{top:100%}.b .wac-tfmark.wac-tfdn::before{bottom:100%}"
            // ⭐ OUR ICONS (`AppIcons`): the tick (was ✓), the menu (was …), the bubble's Mark done.
            + ".wac-tftick{display:inline-flex;align-items:center;justify-content:center}.wac-tftick svg{width:13px;height:13px}"
            + ".wac-tfctl summary{display:inline-flex;align-items:center;padding:3px 6px}.wac-tfctl summary svg{width:18px;height:18px}"
            + ".b .wac-tfmark{display:inline-flex;align-items:center;gap:4px}.b .wac-tfmark svg{width:12px;height:12px}"
            + ".b .wac-tfmark.wac-tfreo svg{display:none}"
            // ⚠ A TRANSLATED bubble keeps its flag beside the clock, bottom right - exactly where this button lands. There
            // it goes to the bottom LEFT, so the flag ("show the original") can still be pressed (audit 2026-10-12).
            + ".b.tr .wac-tfmark{right:auto;left:" + String(AppTaskFrame.SideInset) + "px}"
            + AppTaskFrame.PillCss();
    };

    /**
     * THE OPEN TASKS AS PILLS (owner review 2026-11-01: "show each task as a pill with an empty circle that marks it
     * done"). The tick stays the circle; the label is the button that shows the details; due, who, the link and the
     * "…" menu show only on an opened pill. A late task keeps a red edge so it still reads late while closed.
     * DOCUMENTED EXCEPTION: a constant stylesheet. ERROR-SUPPRESSED-JUSTIFIED: returns a literal.
     */
    public static PillCss = (): string => {
        return ".tasks .tlist{flex-direction:row;flex-wrap:wrap;gap:6px;padding:4px 0}"
            + ".tlist>.tk,.tlist>.tk:first-child{display:inline-flex;max-width:100%;gap:6px;padding:3px 12px 3px 4px;"
            + "border:1px solid var(--line,#e3e6ec);border-radius:14px;background:var(--inbg,#fff);font-size:13px}"
            + ".tmorebox:checked~.tlist>.tk.more{display:inline-flex}"
            + ".tlist>.tk:has(.due.u-overdue){border-color:#e8a29b}"
            + ".tlist>.tk:not(.wac-tfopen) :is(.due,.who,.tgo,.wac-tfctl,.wac-tfmine){display:none}"
            + ".tlist>.tk .tl[data-wac-tfinfo]{cursor:pointer;border-radius:4px}"
            + ".tlist>.tk .tl[data-wac-tfinfo]:focus-visible{outline:2px solid var(--acc,#095495);outline-offset:2px}"
            + ".tlist>.tk.wac-tfopen{flex-basis:100%;flex-wrap:wrap;border-radius:8px;padding:6px 10px 6px 4px}"
            + ".tlist>.tk.wac-tfopen .tl{white-space:normal}"
            + ".wac-tfmine{font-size:12px;color:var(--dim,#6b7385);background:var(--soft,#f0f2f5);border-radius:4px;padding:1px 8px}"
            + ".tlist>.tk .wac-tftick{width:20px;height:20px}";
    };
}

/** Hears how ONE frame change went. A class, not a closure. */
export class AppTaskFrameHeard implements IAppTaskHeard {
    private Owner: AppTaskFrame;
    private Marks: AppTaskFrameMark[];
    private Row: AppRow | null;
    private Before: AppRow | null;
    private Action: string;
    private Until: string;
    private Id: string;

    public constructor(Owner: AppTaskFrame, Marks: AppTaskFrameMark[], Row: AppRow | null, Before: AppRow | null,
                       Action: string, Until: string, Id: string = "") {
        this.Owner = Owner;
        this.Marks = Marks;
        this.Row = Row;
        this.Before = Before;
        this.Action = Action;
        this.Until = Until;
        this.Id = Id;
    }

    public Done = (Task: AppRow | null, Changed: boolean = true): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Task !== null && this.Row !== null) { AppTaskRules.Adopt(this.Row as AppRow, Task as AppRow); }
            this.Owner.Landed(this.Marks, this.Action, this.Until, this.Row, this.Id, Changed);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrameHeard.Done");
        }
    };

    public Failed = (Why: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (this.Row !== null && this.Before !== null) { AppTaskRules.Merge(this.Row as AppRow, this.Before as AppRow); }
            this.Owner.Refused(this.Marks, Why, this.Id);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrameHeard.Failed");
        }
    };
}

/** The end of one task's Undo window. A class, not a closure. */
export class AppTaskFrameUndoEnd {
    private Owner: AppTaskFrame;
    private Id: string;

    public constructor(Owner: AppTaskFrame, Id: string) {
        this.Owner = Owner;
        this.Id = Id;
    }

    public Fire = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Owner.Expire(this.Id);
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppTaskFrameUndoEnd.Fire");
        }
    };
}

/** The frame's own listener on its task list: the header's "N open" chip follows the open count. */
export class AppTaskFrameCount implements IAppTaskListListener {
    private Frame: AppTaskFrame;

    public constructor(Frame: AppTaskFrame) {
        this.Frame = Frame;
    }

    public Changed = (_List: AppTaskList): void => {
        this.Frame.PaintCount();
    };
}
